/**
 * Sistema de Logging Centralizado - Frontend
 *
 * Funcionalidades:
 * - Níveis de log configuráveis (DEBUG, INFO, WARN, ERROR)
 * - ID de sessão único para cada execução
 * - Timestamps claros e formatados
 * - Agrupamento de logs por contexto
 * - Limpeza de console
 * - Controle via variável de ambiente
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

interface LoggerConfig {
  level: LogLevel;
  enabled: boolean;
  showTimestamp: boolean;
  showSessionId: boolean;
  sessionId: string;
}

class Logger {
  private config: LoggerConfig;
  private groups: Map<string, boolean> = new Map();

  constructor() {
    // Gera ID único de sessão
    const sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    // Configuração padrão baseada no ambiente
    const isDevelopment = import.meta.env.DEV;
    const envLogLevel = import.meta.env.VITE_LOG_LEVEL || 'INFO';

    this.config = {
      level: this.parseLogLevel(envLogLevel),
      enabled: isDevelopment, // Desabilita em produção por padrão
      showTimestamp: true,
      showSessionId: isDevelopment,
      sessionId
    };

    // Mensagem de inicialização
    if (this.config.enabled) {
      this.logWelcomeMessage();
    }
  }

  private parseLogLevel(level: string): LogLevel {
    switch (level.toUpperCase()) {
      case 'DEBUG': return LogLevel.DEBUG;
      case 'INFO': return LogLevel.INFO;
      case 'WARN': return LogLevel.WARN;
      case 'ERROR': return LogLevel.ERROR;
      case 'NONE': return LogLevel.NONE;
      default: return LogLevel.INFO;
    }
  }

  private logWelcomeMessage(): void {
    console.log(
      '%c╔═══════════════════════════════════════════════════╗',
      'color: #10b981; font-weight: bold;'
    );
    console.log(
      '%c║  🚀 LawPG - Sistema de Logging Iniciado           ║',
      'color: #10b981; font-weight: bold;'
    );
    console.log(
      `%c║  📝 Sessão: ${this.config.sessionId.substring(0, 20)}... ║`,
      'color: #10b981;'
    );
    console.log(
      `%c║  🎯 Nível: ${LogLevel[this.config.level].padEnd(5, ' ')}                              ║`,
      'color: #10b981;'
    );
    console.log(
      '%c╚═══════════════════════════════════════════════════╝',
      'color: #10b981; font-weight: bold;'
    );
    console.log(' ');
  }

  private shouldLog(level: LogLevel): boolean {
    return this.config.enabled && level >= this.config.level;
  }

  private formatMessage(level: string, context: string, message: string): string {
    const parts: string[] = [];

    if (this.config.showTimestamp) {
      const now = new Date();
      const time = now.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      const milliseconds = now.getMilliseconds().toString().padStart(3, '0');
      parts.push(`[${time}.${milliseconds}]`);
    }

    if (this.config.showSessionId) {
      parts.push(`[${this.config.sessionId.substring(0, 8)}]`);
    }

    parts.push(`[${level}]`);

    if (context) {
      parts.push(`[${context}]`);
    }

    parts.push(message);

    return parts.join(' ');
  }

  private getLogStyle(level: LogLevel): string {
    switch (level) {
      case LogLevel.DEBUG:
        return 'color: #6b7280; font-weight: normal;';
      case LogLevel.INFO:
        return 'color: #3b82f6; font-weight: normal;';
      case LogLevel.WARN:
        return 'color: #f59e0b; font-weight: bold;';
      case LogLevel.ERROR:
        return 'color: #ef4444; font-weight: bold;';
      default:
        return '';
    }
  }

  /**
   * Log de nível DEBUG - Informações detalhadas para desenvolvimento
   */
  debug(context: string, message: string, ...args: unknown[]): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;

    const formattedMessage = this.formatMessage('DEBUG', context, message);
    console.log(`%c${formattedMessage}`, this.getLogStyle(LogLevel.DEBUG), ...args);
  }

  /**
   * Log de nível INFO - Informações gerais
   */
  info(context: string, message: string, ...args: unknown[]): void {
    if (!this.shouldLog(LogLevel.INFO)) return;

    const formattedMessage = this.formatMessage('INFO', context, message);
    console.log(`%c${formattedMessage}`, this.getLogStyle(LogLevel.INFO), ...args);
  }

  /**
   * Log de nível WARN - Avisos
   */
  warn(context: string, message: string, ...args: unknown[]): void {
    if (!this.shouldLog(LogLevel.WARN)) return;

    const formattedMessage = this.formatMessage('WARN', context, message);
    console.warn(`%c${formattedMessage}`, this.getLogStyle(LogLevel.WARN), ...args);
  }

  /**
   * Log de nível ERROR - Erros
   */
  error(context: string, message: string, error?: unknown, ...args: unknown[]): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;

    const formattedMessage = this.formatMessage('ERROR', context, message);
    console.error(`%c${formattedMessage}`, this.getLogStyle(LogLevel.ERROR), error, ...args);

    const stack = (error as { stack?: unknown } | undefined)?.stack;
    if (typeof stack === 'string') {
      console.error('%c📍 Stack Trace:', 'color: #ef4444; font-weight: bold;');
      console.error(stack);
    }
  }

  /**
   * Inicia um grupo de logs (pode ser colapsado)
   */
  group(name: string, collapsed: boolean = false): void {
    if (!this.config.enabled) return;

    const groupName = this.formatMessage('GROUP', '', name);

    if (collapsed) {
      console.groupCollapsed(groupName);
    } else {
      console.group(groupName);
    }

    this.groups.set(name, true);
  }

  /**
   * Finaliza um grupo de logs
   */
  groupEnd(name?: string): void {
    if (!this.config.enabled) return;

    console.groupEnd();

    if (name) {
      this.groups.delete(name);
    }
  }

  /**
   * Log de tabela - Útil para arrays e objetos
   */
  table(context: string, data: unknown, columns?: string[]): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;

    this.info(context, 'Dados tabulados:');
    if (columns) {
      console.table(data, columns);
    } else {
      console.table(data);
    }
  }

  /**
   * Mede tempo de execução
   */
  time(label: string): void {
    if (!this.config.enabled) return;
    console.time(`⏱️  ${label}`);
  }

  /**
   * Finaliza medição de tempo
   */
  timeEnd(label: string): void {
    if (!this.config.enabled) return;
    console.timeEnd(`⏱️  ${label}`);
  }

  /**
   * Limpa o console
   */
  clear(): void {
    console.clear();
    if (this.config.enabled) {
      this.logWelcomeMessage();
      this.info('Logger', 'Console limpo manualmente');
    }
  }

  /**
   * Altera o nível de log em tempo de execução
   */
  setLevel(level: LogLevel): void {
    const oldLevel = this.config.level;
    this.config.level = level;
    this.info('Logger', `Nível de log alterado: ${LogLevel[oldLevel]} → ${LogLevel[level]}`);
  }

  /**
   * Habilita/desabilita logging
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    if (enabled) {
      this.logWelcomeMessage();
      this.info('Logger', 'Logging habilitado');
    } else {
      console.log('%c🔇 Logging desabilitado', 'color: #6b7280;');
    }
  }

  /**
   * Obtém informações da configuração atual
   */
  getConfig(): Readonly<LoggerConfig> {
    return { ...this.config };
  }

  /**
   * Log especial para operações de API
   */
  api(method: string, url: string, status?: number, data?: unknown): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;

    const statusEmoji = status ? (status < 400 ? '✅' : '❌') : '🔄';
    const statusText = status ? `[${status}]` : '[PENDING]';

    this.group(`${statusEmoji} API ${method} ${url} ${statusText}`, true);

    if (data) {
      console.log('%cRequest/Response:', 'font-weight: bold;', data);
    }

    this.groupEnd();
  }

  /**
   * Log especial para mudanças de estado/store
   */
  state(storeName: string, action: string, oldValue?: unknown, newValue?: unknown): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;

    this.group(`🔄 State Change: ${storeName} - ${action}`, true);

    if (oldValue !== undefined) {
      console.log('%cAntes:', 'color: #f59e0b; font-weight: bold;', oldValue);
    }

    if (newValue !== undefined) {
      console.log('%cDepois:', 'color: #10b981; font-weight: bold;', newValue);
    }

    this.groupEnd();
  }

  /**
   * Log especial para navegação
   */
  navigation(from: string, to: string, params?: unknown): void {
    if (!this.shouldLog(LogLevel.INFO)) return;

    const message = `Navegando: ${from} → ${to}`;
    this.info('Navigation', message, params || '');
  }

  /**
   * Cria um banner de separação visual
   */
  separator(title?: string): void {
    if (!this.config.enabled) return;

    console.log(' ');
    if (title) {
      console.log(
        `%c════════════════ ${title} ════════════════`,
        'color: #8b5cf6; font-weight: bold; font-size: 12px;'
      );
    } else {
      console.log('%c═══════════════════════════════════════════════════', 'color: #6b7280;');
    }
    console.log(' ');
  }
}

// Exporta instância singleton
export const logger = new Logger();

// Adiciona ao window para acesso via console do navegador (apenas em dev)
if (import.meta.env.DEV) {
  (window as unknown as { logger: typeof logger }).logger = logger;
  (window as unknown as { LogLevel: typeof LogLevel }).LogLevel = LogLevel;

  // Mensagem de ajuda
  console.log(
    '%c💡 Dica: Use window.logger para controlar logs via console!',
    'color: #8b5cf6; font-style: italic;'
  );
  console.log(
    '%c   Exemplos:',
    'color: #6b7280;'
  );
  console.log(
    '%c   - logger.clear()            → Limpa console',
    'color: #6b7280;'
  );
  console.log(
    '%c   - logger.setLevel(LogLevel.DEBUG) → Mostra tudo',
    'color: #6b7280;'
  );
  console.log(
    '%c   - logger.setLevel(LogLevel.ERROR) → Apenas erros',
    'color: #6b7280;'
  );
  console.log(
    '%c   - logger.setEnabled(false)  → Desabilita logs',
    'color: #6b7280;'
  );
  console.log(' ');
}

export default logger;
